import React, { useState, useEffect, useMemo } from 'react';
import styles from "./branchorder.module.css";
import { getFetch, postFetch } from '../../../fetch/fetch';
import { toast } from 'react-toastify';
import { splitName, SearchIcon, PlusIcon } from './shared';
import AddProductModal from './addproductmodal';
import EditProductModal from './editproductmodal';

const PAGE = 20;

// 관리자페이지 — 물품 × 지점 노출 토글 + 물품별 하루 주문 한도 (본사직원만, 서버도 todoistApiKey 확인)
const AdminPage: React.FC<any> = (props: any) => {

    const branches: any[] = props.init.branches;
    const [items, setItems] = useState<any[]>([]);
    const [categories, setCategories] = useState<string[]>([]);
    // "vendorItemId|branchKey" 가 있으면 숨김
    const [hidden, setHidden] = useState<Set<string>>(new Set());
    const [cat, setCat] = useState("전체");
    const [q, setQ] = useState("");
    const [page, setPage] = useState(0);
    const [saving, setSaving] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    // 옵션링크·물품명 수정 대상 물품 (null = 모달 닫힘)
    const [editTarget, setEditTarget] = useState<any>(null);

    useEffect(() => {
        getMatrix();
    }, []);

    const getMatrix = async () => {
        try {
            const result: any = await getFetch("/api/branch-orders/admin/matrix");
            if (result.message === "success") {
                setItems(result.data.items);
                setCategories(result.data.categories);
                setHidden(new Set(result.data.hidden.map((h: any) => `${h.id}|${h.branchKey}`)));
            } else {
                toast.error("노출 설정을 불러오지 못했습니다.");
            }
        } catch (e) {
            console.log(e);
        }
    }

    const visible = (id: string, b: string) => !hidden.has(`${id}|${b}`);

    const list = useMemo(() => {
        const qq = q.trim().toLowerCase();
        return items.filter((i) => (cat === "전체" || i.category === cat) && (!qq || ("" + i.name).toLowerCase().includes(qq)));
    }, [items, cat, q]);

    const pages = Math.max(1, Math.ceil(list.length / PAGE));
    const curPage = Math.min(page, pages - 1);
    const slice = list.slice(curPage * PAGE, (curPage + 1) * PAGE);

    // 화면 먼저 바꾸고 저장, 실패하면 다시 불러옴
    const saveVisibility = async (ids: string[], branchKeys: string[], on: boolean, message: string) => {
        if (saving || !ids.length) return;
        const next = new Set(hidden);
        ids.forEach((id) => branchKeys.forEach((b) => { if (on) next.delete(`${id}|${b}`); else next.add(`${id}|${b}`); }));
        setHidden(next);
        setSaving(true);
        try {
            const result: any = await postFetch("/api/branch-orders/admin/visibility", { vendorItemIds: ids, branchKeys, visible: on });
            if (result.message === "success") {
                toast(message, { autoClose: 1500 });
            } else {
                toast.error(`저장 실패 (${result.message})`);
                getMatrix();
            }
        } catch (e) {
            console.log(e);
            toast.error("저장 실패");
            getMatrix();
        }
        setSaving(false);
    }

    const toggleColumn = (b: any) => {
        const allOn = list.every((i) => visible(i.id, b.key));
        saveVisibility(list.map((i) => i.id), [b.key], !allOn, `${b.label} · ${cat} ${list.length}종 ${allOn ? "숨김" : "노출"} 처리`);
    }

    const toggleRow = (id: string) => {
        const allOn = branches.every((b) => visible(id, b.key));
        saveVisibility([id], branches.map((b) => b.key), !allOn, "노출 설정을 저장했습니다.");
    }

    const toggleCell = (id: string, b: string) => {
        saveVisibility([id], [b], !visible(id, b), "노출 설정을 저장했습니다.");
    }

    // 하루 한도 저장 (빈칸 = 무제한). 같은 값이면 저장 안 함
    const saveDailyLimit = async (i: any, raw: string) => {
        const text = raw.trim();
        const next = text === "" ? null : +text;
        if (next !== null && (!Number.isInteger(next) || next < 0 || next > 9999)) {
            toast.error("하루 한도는 0~9999 사이 숫자로 입력해 주세요 (비우면 무제한).");
            getMatrix();
            return;
        }
        const prev = i.dailyLimit === null || i.dailyLimit === undefined ? null : +i.dailyLimit;
        if (prev === next) return;
        setItems((list) => list.map((x) => x.id === i.id ? { ...x, dailyLimit: next } : x));
        try {
            const result: any = await postFetch("/api/branch-orders/admin/daily-limit", { vendorItemId: i.id, dailyLimit: next });
            if (result.message === "success") {
                toast(next === null ? "하루 한도: 무제한" : `하루 한도: 지점별 하루 ${next}개`, { autoClose: 1500 });
            } else {
                toast.error(`저장 실패 (${result.message})`);
                getMatrix();
            }
        } catch (e) {
            console.log(e);
            toast.error("저장 실패");
            getMatrix();
        }
    }

    const handleAdded = () => {
        setAddOpen(false);
        setCat("전체");
        setQ("");
        setPage(0);
        getMatrix();
        props.reloadInit();
    }

    // 수정 저장 후: 표 다시 불러오고 주문 화면 카탈로그(새 링크·옵션 ID)도 갱신
    const handleEdited = () => {
        setEditTarget(null);
        getMatrix();
        props.reloadInit();
    }

    return (
        <section>
            <div className={styles.sec}>
                <h2>물품 노출 설정</h2>
                <span className={styles.sub}>지점별로 주문 화면에 보일 물품을 켜고 끕니다 · 끈 물품은 해당 지점 직원에게 보이지 않습니다 · 하루 한도는 지점별 하루 주문 수량 (비우면 무제한)</span>
                <div className={styles.right}>
                    <span className={styles.sub}>{items.length}종 · 숨김 설정 {hidden.size}건</span>
                    <button type="button" className={`${styles.btn} ${styles.primary}`} onClick={() => setAddOpen(true)}><PlusIcon />물품 추가</button>
                </div>
            </div>
            <div className={styles.panel}>
                <div className={styles.catHead}>
                    <div className={`${styles.seg} ${styles.compact}`} aria-label="분류" style={{ margin: 0 }}>
                        {["전체", ...categories].map((c) => <button key={c} type="button" className={c === cat ? styles.on : ""} onClick={() => { setCat(c); setPage(0); }}>{c}</button>)}
                    </div>
                    <label className={styles.search}><SearchIcon /><input type="search" placeholder="물품명으로 검색" autoComplete="off" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} /></label>
                </div>
                <div className={styles.tblWrap}>
                    <table className={`${styles.table} ${styles.admin}`}>
                        <thead>
                            <tr>
                                <th>물품</th>
                                <th style={{ width: 76 }}>분류</th>
                                <th style={{ width: 120 }}>옵션링크<small>쿠팡</small></th>
                                <th style={{ width: 84 }}>하루 한도<small>지점별</small></th>
                                <th className={styles.b}>전체<small>모든 지점</small></th>
                                {branches.map((b) => {
                                    const onCnt = list.filter((i) => visible(i.id, b.key)).length;
                                    const cls = onCnt === list.length ? styles.on : onCnt === 0 ? "" : `${styles.on} ${styles.half}`;
                                    return (
                                        <th key={b.key} className={styles.b}>
                                            {b.label}<small>{onCnt}/{list.length}</small>
                                            <button type="button" className={`${styles.sw} ${styles.sm} ${cls}`} disabled={saving} aria-label={`${b.label} 전체 전환`}
                                                title={`이 분류 전체 ${onCnt === list.length ? "끄기" : "켜기"}`} onClick={() => toggleColumn(b)}></button>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {
                                slice.length ? slice.map((i) => {
                                    const [n, o] = splitName(i.name);
                                    const offAll = branches.every((b) => !visible(i.id, b.key));
                                    const onAll = branches.every((b) => visible(i.id, b.key));
                                    return (
                                        <tr key={i.id} className={offAll ? styles.off : ""}>
                                            <td className={styles.adminName}><span className={styles.n} title={i.name}>{n}{i.isNew ? <span className={styles.tagNew}>신규</span> : null}</span><span className={styles.o}>{o}</span></td>
                                            <td><span className={styles.mini}>{i.category}</span></td>
                                            <td style={{ whiteSpace: "nowrap" }}>
                                                {i.url ? <a className={styles.link} href={i.url} target="_blank" rel="noopener noreferrer" title={i.url}>옵션링크</a> : <span className={styles.link}>링크 없음</span>}
                                                <button type="button" className={styles.linkbtn} onClick={() => setEditTarget(i)}>수정</button>
                                            </td>
                                            <td>
                                                <input key={`${i.id}-${i.dailyLimit}`} type="number" min={0} max={9999} className={styles.limitInput} placeholder="무제한"
                                                    defaultValue={i.dailyLimit === null || i.dailyLimit === undefined ? "" : i.dailyLimit} aria-label="하루 주문 한도"
                                                    onBlur={(e) => saveDailyLimit(i, e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
                                            </td>
                                            <td className={styles.b}>
                                                <button type="button" className={`${styles.sw} ${onAll ? styles.on : offAll ? "" : `${styles.on} ${styles.half}`}`} disabled={saving} aria-label="전 지점 전환" onClick={() => toggleRow(i.id)}></button>
                                            </td>
                                            {branches.map((b) => {
                                                const v = visible(i.id, b.key);
                                                return (
                                                    <td key={b.key} className={styles.b}>
                                                        <button type="button" className={`${styles.sw} ${styles.sm} ${v ? styles.on : ""}`} disabled={saving} aria-label={`${b.label} ${v ? "노출 중" : "숨김"}`} aria-pressed={v} onClick={() => toggleCell(i.id, b.key)}></button>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })
                                    : <tr><td colSpan={5 + branches.length} className={styles.empty}>해당 조건의 물품이 없습니다.</td></tr>
                            }
                        </tbody>
                    </table>
                </div>
                <div className={styles.pager} style={{ paddingTop: 14 }}>
                    <button type="button" disabled={curPage === 0} onClick={() => setPage(curPage - 1)}>이전</button>
                    <span className={styles.num}>{curPage + 1} / {pages}</span>
                    <button type="button" disabled={curPage >= pages - 1} onClick={() => setPage(curPage + 1)}>다음</button>
                </div>
            </div>

            {editTarget && <EditProductModal item={editTarget} existingIds={new Set(items.map((i) => i.id))} onClose={() => setEditTarget(null)} onSaved={handleEdited} />}
            {addOpen && <AddProductModal branches={branches} categories={categories} defaultCategory={cat} existingIds={new Set(items.map((i) => i.id))} onClose={() => setAddOpen(false)} onAdded={handleAdded} />}
        </section>
    );
}

export default AdminPage;
